import React from 'react'
import Pane from 'components/Common/Pane'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { getPlural, pluralizeToString } from 'lib/string/pluralize'
import { OfferPageMultiRoomAutoselectionReason, OfferPageMultiRoomMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import noop from 'lib/function/noop'
import MessageBanner from 'LuxKit/Banners/MessageBanner'

export const ROOM_MODE_TOGGLE_SECTION_ID = 'roomModeToggleSection'

interface Props {
  mode?: OfferPageMultiRoomMode;
  onChange?: (mode: OfferPageMultiRoomMode) => void;
  rooms: Array<OfferPageRoom>;
  saleUnit: string;
  autoselectionReason?: OfferPageMultiRoomAutoselectionReason;
}

function OfferPageRoomModeToggleSection({
  mode = 'individual',
  onChange = noop,
  rooms,
  saleUnit,
  autoselectionReason,
}: Props) {
  const roomsAreSelected = rooms.some(room => room.roomId)
  const showPreSelectionBanner = roomsAreSelected && mode === 'individual' && autoselectionReason === 'initial-selection'
  const showInvalidSelectionBanner = roomsAreSelected && mode === 'individual' && autoselectionReason === 'invalid-selections'

  return <Pane padding="20 20 16" id={ROOM_MODE_TOGGLE_SECTION_ID}>
    <VerticalSpacer gap={16}>
      <Heading variant="heading4">Select your {pluralizeToString(saleUnit, rooms.length)}</Heading>
      <VerticalSpacer gap={12}>
        <BodyText variant="medium">
          Choose the same package for all {getPlural(saleUnit)}, or customise each {saleUnit} individually.
        </BodyText>
        <SegmentedOptionsGroup onValueChange={onChange} inputsName="room-mode">
          <SegmentedOption
            title="Individually"
            value="individual"
            checked={mode === 'individual'}
          />
          <SegmentedOption
            title="Same for all rooms"
            value="same-room"
            checked={mode === 'same-room'}
          />
        </SegmentedOptionsGroup>
      </VerticalSpacer>
      {showPreSelectionBanner && <MessageBanner
        kind="promotional-primary"
        density="compact"
      >
        We've pre-selected the best-value {getPlural(saleUnit)} to get you started. Customise each {saleUnit} below.
      </MessageBanner>}
      {showInvalidSelectionBanner && <MessageBanner
        kind="warning"
        density="compact"
      >
        Your previous {saleUnit} selection is no longer available, please review your new selection.
      </MessageBanner>}
    </VerticalSpacer>
  </Pane>
}

export default OfferPageRoomModeToggleSection
